/* ==========================================================
   HELENE CLARKSON DESIGN
   Collection Product Swatches
   Version 2.0
   Compatible with Dawn 15.5+

   Behaviour:
   - Hover previews a colour temporarily
   - Click permanently selects a colour
   - Moving away restores the clicked colour
   - Product image and title links update to the selected variant
========================================================== */

(() => {
  const SWATCH_SELECTOR = '[data-hcd-card-swatch]';
  const SWATCH_GROUP_SELECTOR = '[data-hcd-card-swatches]';

  /**
   * Find the product card that contains a swatch.
   */
  function getCard(swatch) {
    return (
      swatch.closest('.card-wrapper') ||
      swatch.closest('.card') ||
      swatch.closest('li.grid__item')
    );
  }

  /**
   * Find all relevant product images inside a card.
   * Dawn may include both a main image and a hover image.
   */
  function getCardImages(card) {
    if (!card) return [];

    const images = card.querySelectorAll(
      '.card__media img, .card__inner img, .media img'
    );

    return Array.from(images).filter(
      (image, index, imageList) => imageList.indexOf(image) === index
    );
  }

  /**
   * Update the product image using the data stored on a swatch.
   */
  function updateCardImage(card, swatch) {
    if (!card || !swatch) return;

    const imageSrc = swatch.dataset.imageSrc;
    const imageSrcset = swatch.dataset.imageSrcset;
    const imageAlt = swatch.dataset.imageAlt;

    if (!imageSrc) return;

    const images = getCardImages(card);

    if (!images.length) return;

    images.forEach((image) => {
      image.classList.add('hcd-card-image--changing');

      window.setTimeout(() => {
        image.src = imageSrc;

        if (imageSrcset) {
          image.srcset = imageSrcset;
        } else {
          image.removeAttribute('srcset');
        }

        if (imageAlt) {
          image.alt = imageAlt;
        }

        image.classList.remove('hcd-card-image--changing');
      }, 100);
    });
  }

  /**
   * Move the selected ring to the clicked swatch.
   */
  function updateSelectedSwatch(container, selectedSwatch) {
    if (!container || !selectedSwatch) return;

    container.querySelectorAll(SWATCH_SELECTOR).forEach((swatch) => {
      const isSelected = swatch === selectedSwatch;

      swatch.classList.toggle('hcd-card-swatch--selected', isSelected);

      if (isSelected) {
        swatch.setAttribute('aria-current', 'true');
      } else {
        swatch.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Update the image and title links so they open the selected variant.
   */
  function updateCardLinks(card, variantUrl) {
    if (!card || !variantUrl) return;

    const productLinks = card.querySelectorAll(
      'a[href*="/products/"]:not([data-hcd-card-swatch])'
    );

    productLinks.forEach((link) => {
      link.href = variantUrl;
    });
  }

  /**
   * Store the selected swatch on the card.
   */
  function saveSelectedSwatch(card, swatch) {
    if (!card || !swatch) return;

    const variantId = swatch.dataset.variantId || '';
    const variantUrl =
      swatch.dataset.variantUrl || swatch.getAttribute('href') || '';

    card.dataset.hcdSelectedVariantId = variantId;
    card.dataset.hcdSelectedVariantUrl = variantUrl;
  }

  /**
   * Find the swatch currently selected for a card.
   */
  function getSelectedSwatch(card) {
    if (!card) return null;

    const selectedVariantId = card.dataset.hcdSelectedVariantId;

    if (selectedVariantId) {
      const matchingSwatch = card.querySelector(
        `${SWATCH_SELECTOR}[data-variant-id="${CSS.escape(
          selectedVariantId
        )}"]`
      );

      if (matchingSwatch) return matchingSwatch;
    }

    return (
      card.querySelector(
        `${SWATCH_SELECTOR}.hcd-card-swatch--selected`
      ) || card.querySelector(SWATCH_SELECTOR)
    );
  }

  /**
   * Permanently select a swatch.
   */
  function selectSwatch(swatch) {
    const container = swatch.closest(SWATCH_GROUP_SELECTOR);
    const card = getCard(swatch);

    if (!container || !card) return;

    const variantUrl =
      swatch.dataset.variantUrl || swatch.getAttribute('href');

    saveSelectedSwatch(card, swatch);
    updateSelectedSwatch(container, swatch);
    updateCardImage(card, swatch);
    updateCardLinks(card, variantUrl);
  }

  /**
   * Temporarily preview a swatch while hovering.
   */
  function previewSwatch(swatch) {
    const card = getCard(swatch);

    if (!card) return;

    updateCardImage(card, swatch);
  }

  /**
   * Restore the permanently selected colour after hover ends.
   */
  function restoreSelectedSwatch(card) {
    if (!card) return;

    const selectedSwatch = getSelectedSwatch(card);

    if (!selectedSwatch) return;

    updateCardImage(card, selectedSwatch);
  }

  /**
   * Initialize every visible product card.
   */
  function initializeSwatches(root = document) {
    const groups = root.querySelectorAll(SWATCH_GROUP_SELECTOR);

    groups.forEach((container) => {
      const card =
        container.closest('.card-wrapper') ||
        container.closest('.card') ||
        container.closest('li.grid__item');

      if (!card || card.dataset.hcdSwatchesInitialized === 'true') {
        return;
      }

      const initialSwatch =
        container.querySelector(
          `${SWATCH_SELECTOR}.hcd-card-swatch--selected`
        ) || container.querySelector(SWATCH_SELECTOR);

      if (initialSwatch) {
        saveSelectedSwatch(card, initialSwatch);
        updateSelectedSwatch(container, initialSwatch);
      }

      card.dataset.hcdSwatchesInitialized = 'true';
    });
  }

  /**
   * Click:
   * Select the colour without opening the product page.
   */
  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target instanceof Element ? event.target : null;

      if (!target) return;

      const swatch = target.closest(SWATCH_SELECTOR);

      if (!swatch) return;

      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      selectSwatch(swatch);
    },
    true
  );

  /**
   * Hover:
   * Temporarily show the hovered colour.
   */
  document.addEventListener('mouseover', (event) => {
    const target =
      event.target instanceof Element ? event.target : null;

    if (!target) return;

    const swatch = target.closest(SWATCH_SELECTOR);

    if (!swatch) return;

    previewSwatch(swatch);
  });

  /**
   * Mouse leaves an individual swatch:
   * Restore the clicked colour unless moving directly to another swatch.
   */
  document.addEventListener('mouseout', (event) => {
    const target =
      event.target instanceof Element ? event.target : null;

    if (!target) return;

    const swatch = target.closest(SWATCH_SELECTOR);

    if (!swatch) return;

    const relatedTarget =
      event.relatedTarget instanceof Element
        ? event.relatedTarget
        : null;

    if (
      relatedTarget &&
      relatedTarget.closest(SWATCH_SELECTOR)
    ) {
      return;
    }

    const card = getCard(swatch);

    restoreSelectedSwatch(card);
  });

  /**
   * Initialize when the page first loads.
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeSwatches();
    });
  } else {
    initializeSwatches();
  }

  /**
   * Reinitialize after Shopify dynamically redraws a collection grid.
   */
  document.addEventListener('shopify:section:load', (event) => {
    initializeSwatches(event.target);
  });
})();